/**
 * Workflow Dispatch Hook
 * Manages GitHub workflow dispatch state and polling
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  dispatchWorkflow,
  findRecentDispatchedRun,
  getWorkflowRun,
  isTerminalStatus,
} from '../api/github';
import { useEchoRunMutations, useEchoStreamerMutations } from '../api/echo';
import type { GitHubWorkflowRun, WorkflowDispatchInput, EchoRun, EchoRunCreate } from '../api/types/echo';
import { buildSegmentPrompt, buildMasterPrompt, getEchoConfig } from '../config/echo';
import { detectPlatform, parseVideoUrl } from '../utils/urlParser';

// Internal form input that maps to WorkflowDispatchInput
export interface WorkflowFormInput {
  url: string;
  streamerName: string;
  date: string;
  promptTemplate?: string;
  flaggedTerms?: string;
  emailRecipients?: string;
  /** ISO 639-1 language code for transcription; "" = auto-detect. */
  language?: string;
}

export interface WorkflowDispatchState {
  isDispatching: boolean;
  isPolling: boolean;
  run: GitHubWorkflowRun | null;
  echoRun: EchoRun | null;
  error: string | null;
}

export interface WorkflowDispatchActions {
  dispatch: (input: WorkflowFormInput, matterId: number, userEmail: string) => Promise<void>;
  reset: () => void;
}

export interface UseWorkflowDispatchResult extends WorkflowDispatchState, WorkflowDispatchActions {}

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 120; // 10 minutes max

/**
 * Hook for managing workflow dispatch and status polling
 */
export function useWorkflowDispatch(): UseWorkflowDispatchResult {
  const [state, setState] = useState<WorkflowDispatchState>({
    isDispatching: false,
    isPolling: false,
    run: null,
    echoRun: null,
    error: null,
  });

  const pollIntervalRef = useRef<number | null>(null);
  const pollCountRef = useRef(0);
  const { createEchoRun, updateEchoRun } = useEchoRunMutations();
  const { findOrCreateStreamer } = useEchoStreamerMutations();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        window.clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollCountRef.current = 0;
    setState((prev) => ({ ...prev, isPolling: false }));
  }, []);

  // Poll for workflow status
  const pollStatus = useCallback(async (runId: number, echoRunId: string) => {
    try {
      pollCountRef.current += 1;

      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        stopPolling();
        setState((prev) => ({
          ...prev,
          error: 'Polling timed out. Check GitHub Actions for status.',
        }));
        return;
      }

      const updatedRun = await getWorkflowRun(runId);
      if (!updatedRun) {
        return; // Continue polling
      }

      setState((prev) => ({ ...prev, run: updatedRun }));

      // Update Echo run status if terminal
      if (isTerminalStatus(updatedRun)) {
        stopPolling();

        // Map GitHub status to Echo status (capitalized to match schema)
        let echoStatus: 'Completed' | 'Failed' = 'Completed';
        if (updatedRun.conclusion === 'failure' || updatedRun.conclusion === 'cancelled') {
          echoStatus = 'Failed';
        }

        try {
          const updated = await updateEchoRun(parseInt(echoRunId, 10), { status: echoStatus });
          if (updated) {
            setState((prev) => ({ ...prev, echoRun: updated }));
          }
        } catch (err) {
          console.error('Failed to update Echo run status:', err);
        }
      }
    } catch (err) {
      console.error('Error polling workflow status:', err);
      // Continue polling on errors
    }
  }, [stopPolling, updateEchoRun]);

  // Start polling for a specific run
  const startPolling = useCallback((runId: number, echoRunId: string) => {
    stopPolling();
    setState((prev) => ({ ...prev, isPolling: true }));

    pollIntervalRef.current = window.setInterval(() => {
      pollStatus(runId, echoRunId);
    }, POLL_INTERVAL_MS);

    // Initial poll immediately
    pollStatus(runId, echoRunId);
  }, [pollStatus, stopPolling]);

  // Dispatch workflow
  const dispatch = useCallback(async (
    input: WorkflowFormInput,
    matterId: number,
    userEmail: string
  ) => {
    setState({
      isDispatching: true,
      isPolling: false,
      run: null,
      echoRun: null,
      error: null,
    });

    try {
      // Build the complete prompts
      const segmentPrompt = buildSegmentPrompt(input.promptTemplate || '', input.flaggedTerms || '');
      const masterPrompt = buildMasterPrompt(segmentPrompt);

      // Resolve the streamer: find existing or create new one
      const platform = detectPlatform(input.url);
      const streamerPlatform = platform === 'unknown' ? 'upload' : platform;
      const streamer = await findOrCreateStreamer(matterId, input.streamerName, streamerPlatform);

      if (!streamer) {
        setState((prev) => ({
          ...prev,
          isDispatching: false,
          error: 'Failed to resolve streamer. Please try again.',
        }));
        return;
      }

      // Create Echo run record FIRST so we can pass the ID to the workflow
      const { S3_BUCKET } = getEchoConfig();
      // Slugify streamer name to match the format used by the workflow in S3
      const slugifiedName = input.streamerName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      // Extract video ID from URL to use as unique title segment
      const parsedUrl = parseVideoUrl(input.url);
      const videoTitle = parsedUrl.videoId || new Date().getTime().toString();
      const s3StorageLocation = `s3://${S3_BUCKET}/${slugifiedName}/${input.date}/${videoTitle}`;

      let echoRun: EchoRun | null = null;
      try {
        const runCreate: EchoRunCreate = {
          echo_streamer_id: streamer.id,
          account_id: userEmail,
          prompt: segmentPrompt,
          status: 'Processing',
          s3_storage_location: s3StorageLocation,
        };

        echoRun = await createEchoRun(runCreate) ?? null;

        if (echoRun) {
          setState((prev) => ({ ...prev, echoRun }));
        }
      } catch (err) {
        console.error('Failed to create Echo run:', err);
        // Continue with dispatch even if tracking record fails
      }

      // Build workflow input with Echo run ID for server-side status updates
      const workflowInput: WorkflowDispatchInput = {
        url: input.url,
        streamerName: input.streamerName,
        date: input.date,
        segmentPrompt: segmentPrompt,
        masterPrompt: masterPrompt,
        recipients: input.emailRecipients || '',
        echoRunId: echoRun?.run_id ? String(echoRun.run_id) : '',
        language: input.language || '',
      };

      // Dispatch the GitHub workflow
      const dispatchTime = new Date();
      const success = await dispatchWorkflow(workflowInput);

      if (!success) {
        // Mark the echo run as failed if dispatch itself failed
        if (echoRun?.run_id) {
          try {
            await updateEchoRun(echoRun.run_id, { status: 'Failed' });
          } catch { /* best effort */ }
        }
        setState((prev) => ({
          ...prev,
          isDispatching: false,
          error: 'Failed to dispatch workflow. Check GitHub token and permissions.',
        }));
        return;
      }

      // Wait for the run to appear
      setState((prev) => ({ ...prev, isPolling: true }));
      const githubRun = await findRecentDispatchedRun(dispatchTime);

      if (!githubRun) {
        setState((prev) => ({
          ...prev,
          isDispatching: false,
          isPolling: false,
          error: 'Workflow dispatched but could not find the run. Check GitHub Actions.',
        }));
        return;
      }

      setState((prev) => ({ ...prev, run: githubRun, isDispatching: false }));

      // Start polling for status updates
      if (echoRun?.run_id) {
        startPolling(githubRun.id, String(echoRun.run_id));
      }
    } catch (err) {
      console.error('Dispatch error:', err);
      setState((prev) => ({
        ...prev,
        isDispatching: false,
        error: err instanceof Error ? err.message : 'Failed to dispatch workflow',
      }));
    }
  }, [createEchoRun, updateEchoRun, startPolling, findOrCreateStreamer]);

  // Reset state
  const reset = useCallback(() => {
    stopPolling();
    setState({
      isDispatching: false,
      isPolling: false,
      run: null,
      echoRun: null,
      error: null,
    });
  }, [stopPolling]);

  return {
    ...state,
    dispatch,
    reset,
  };
}

export default useWorkflowDispatch;
